import React, { useState } from 'react';
import { X } from 'lucide-react';

export default function CreateRoleModal({ onClose, onSave, allRoles = [] }) {
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    hierarchyLevel: 50
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate role name format (lowercase, underscores only)
      const roleNameRegex = /^[a-z_]+$/;
      if (!roleNameRegex?.test(formData?.name)) {
        throw new Error('Role name must be lowercase with underscores only');
      }

      await onSave(formData);
    } catch (err) {
      setError(err?.message);
      setLoading(false);
    }
  };

  // Hierarchy level options ordered from highest to lowest authority
  const hierarchyLevels = [
    { value: 5, label: '1 - Super Administrator', description: 'Highest authority, system-wide access' },
    { value: 15, label: '2 - Business Administrator', description: 'Tenant-level administration' },
    { value: 30, label: '3 - Manager', description: 'Team and department management' },
    { value: 50, label: '4 - Staff', description: 'Standard user access' },
    { value: 80, label: '5 - Customer', description: 'External customer access' }
  ];

  // Filter roles that can be overseen (only roles with lower authority / higher hierarchy numbers)
  // CRITICAL: Super Admin (hierarchy <= 10) should NEVER appear in oversight for non-Super Admin roles
  const availableRoles = allRoles?.filter(r => {
    // Exclude Super Admin from oversight options for all non-Super Admin roles
    if (r?.hierarchyLevel <= 10 && formData?.hierarchyLevel > 10) return false;
    
    // Only show roles with lower authority (higher hierarchy numbers)
    return r?.hierarchyLevel > formData?.hierarchyLevel;
  });

  const getHierarchyBadge = (level) => {
    const levelMap = {
      5: 'Super Administrator',
      15: 'Business Administrator',
      30: 'Manager',
      50: 'Staff',
      80: 'Customer'
    };
    return levelMap?.[level] || 'Unknown';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Create New Role</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role Name *
            </label>
            <input
              type="text"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              placeholder="e.g., sales_manager"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Lowercase letters and underscores only
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name *
            </label>
            <input
              type="text"
              value={formData?.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e?.target?.value })}
              placeholder="e.g., Sales Manager"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              placeholder="Describe the role's responsibilities..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hierarchy Level *
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Select the authority level for this role (ordered from highest to lowest)
            </p>
            <div className="space-y-2">
              {hierarchyLevels?.map((level) => {
                const isSelected = formData?.hierarchyLevel === level?.value;
                return (
                  <label
                    key={level?.value}
                    className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-50' :'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => setFormData({ ...formData, hierarchyLevel: level?.value })}
                        className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {level?.label}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {level?.description}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}